import {VanillaReaderNavigationTree} from "./VanillaReaderNavigationTree";
import {
    IVanillaReaderNavigationCollection,
    IVanillaReaderNavigationItem,
    IVanillaReaderNavigationTreeData,
    IVanillaReaderReadingProgressionTimeline,
    IVanillaReaderReadingProgressionTimelineData,
    IVanillaReaderReadingProgressionTimelinePositionData,
    IVanillaReaderSyntheticPageData,
    IVanillaReaderSyntheticPageListData,
    VanillaReaderPaginationType,
    VanillaReaderSyntheticPaginationSegmentationMethod
} from "./VanillaReaderModel";
import {ILocator} from "@colibrio/colibrio-reader-framework/colibrio-core-locator";
import {
    ContentPositionTimelineUnit,
    NavigationCollectionType
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import {IIntegerRange} from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import {IntRange} from "../utils/IntRange";

/*
* # VanillaReaderView
*
* ## PRIMARY COLIBRIO FRAMEWORK TYPES
*
* - IContentPositionTimeline
* - IReaderPublicationNavigation
*
* ## RESPONSIBILITIES
*
* This class builds a list of "synthetic pages". A synthetic page represents either,
*
* - a range of a specific number of words, constrained by a so-called section.
* - a range of content based on an entry in the EPUB's page list collection.
* - an entire content document, if the publication is a fixed layout EPUB, or is a PDF.
*
* The reason to use these synthetic pages instead of using actual rendered "on-screen pages" is that the synthetic variant
* always points to the location, regardless of screen size, font-size etc.
*
* As mentioned above the implementation generalises different definitions of "pages" or locations to a common type
* called `IVanillaReaderSyntheticPageData`. This type is a composite type withing the `IVanillaReaderSyntheticPageListData`
* type that divides a publication into logical sections. A section will map to `ContentDocument` in EPUB and a page in
* the case of a PDF.
*
* One of the reasons for this containing type is to be able to map pages to a meaningful and useful structure. Without
* this mapping it would be up to reading system implementation to "shift" the location to the nearest meaningful position,
* such as a chapter or a part, when exposing the information to the user, thus making the synthetic locator ambiguous.
*
* The `IVanillaReaderSyntheticPageListData` type also lets you specify the algorithm that was used to calculate the ranges
* of the synthetic pages. In the best of worlds there would be an agreed upon set of algorithms, each mapping to a
* specific family of character sets.
*
* */
export class VanillaReaderPublicationPageList {
    private _pageList: IVanillaReaderSyntheticPageData[] = [];
    private _activePage: IVanillaReaderSyntheticPageData | undefined;
    private readonly _navigationTreeData: IVanillaReaderNavigationTreeData;
    private _timelineData: IVanillaReaderReadingProgressionTimelineData | undefined;

    constructor(private _vanillaReaderProgressionTimeline: IVanillaReaderReadingProgressionTimeline,
                private _vanillaReaderNavigationTree: VanillaReaderNavigationTree,
                private _pageSizeInWordCount: number = 500) {

            this._navigationTreeData = this._vanillaReaderNavigationTree.getTreeData();
    }

    async build(): Promise<IVanillaReaderSyntheticPageListData | undefined> {
        if(!this._timelineData) {
            this._timelineData = await this._vanillaReaderProgressionTimeline.serialize();

            if(!this._timelineData) {
                throw new Error('VanillaReaderPublicationPageList.build(), Unable to serialize VanillaReaderProgressionTimeline instance.');
            }
        }

        let hasPageList = this._navigationTreeData.collections.find(collection => collection.type === NavigationCollectionType.PAGE_LIST);

        if(!hasPageList) {
            this._pageList = await this._buildSyntheticPageListFromTimelineData(this._timelineData, this._pageSizeInWordCount);
        } else {
            this._pageList = await this._buildSyntheticPageListFromNavTreeData(this._navigationTreeData);
        }

        return this.serialize();

    }

    public get pageSize(): number {
        return this._pageSizeInWordCount;
    }

    serialize(): IVanillaReaderSyntheticPageListData {
        let paginationType = this.paginationType;
        let syntheticPaginationSegmentationMethod: VanillaReaderSyntheticPaginationSegmentationMethod | undefined =
            paginationType === VanillaReaderPaginationType.SYNTHETIC ?
            VanillaReaderSyntheticPaginationSegmentationMethod.UNICODE_WHITESPACE_SEGMENTATION : undefined;

        return {
            pages: this._pageList,
            paginationType,
            syntheticPaginationSegmentationMethod
        };
    }

    public get pageCount(): number {
        return this._pageList.length;
    }

    public get paginationType(): VanillaReaderPaginationType {
        let returnValue: VanillaReaderPaginationType;

        let pageList = this._navigationTreeData?.collections.find(collection => collection.type === NavigationCollectionType.PAGE_LIST);

        switch (this._timelineData?.unit) {
            case ContentPositionTimelineUnit.PAGES:
                returnValue = VanillaReaderPaginationType.SECTIONS;
                break;
            case ContentPositionTimelineUnit.DOCUMENTS:
                returnValue = VanillaReaderPaginationType.SECTIONS;
                break;
            default:
                returnValue = pageList ? VanillaReaderPaginationType.PAGE_LIST : VanillaReaderPaginationType.SYNTHETIC;
        }
        return returnValue;

    }

    public get activeSyntheticPage(): IVanillaReaderSyntheticPageData | undefined {
        return this._activePage;
    }
    public async fetchPagesForLocator(locator: ILocator): Promise<IVanillaReaderSyntheticPageData[] | undefined> {
        const timelineRange = await this._vanillaReaderProgressionTimeline.fetchTimelineRange(locator);

        if(!timelineRange) {
            console.warn('VanillaReaderPublicationPageList.getPagesInVisibleRange(), visible range locator did not resolve to any timeline range.', locator);
            return;
        }
        let startIndex = timelineRange.start;
        let endIndex = timelineRange.end;
        // If this is not a range, we "expand it".
        if(timelineRange.start === timelineRange.end) {
            endIndex = endIndex + 1;
        }

        return this._pageList.filter(page => {
            return page.range.intersects(new IntRange(startIndex, endIndex));
        });
    }

    /**
     *
     * PRIVATE METHODS
     *
     * */

    private async _buildSyntheticPageListFromTimelineData(timelineData: IVanillaReaderReadingProgressionTimelineData, pageSize: number): Promise<IVanillaReaderSyntheticPageData[]> {
        let pageList: IVanillaReaderSyntheticPageData[] = [];

        if(timelineData.sections) {

            let currentPageIndex: number = 0;
            for (let section of timelineData.sections) {

                let currentSectionStartPosition: number = section.range.start;
                let currentSectionEndPosition: number = section.range.end;
                let nextPageStartPosition: number = currentSectionStartPosition;

                // If it's a fixed layout publication, such as a PDF, the range will have the same start and end index.
                // In the while loop below we expect an "end exclusive" range however, so we need to increment the
                // end by one index.
                if(timelineData.unit === ContentPositionTimelineUnit.PAGES && currentSectionStartPosition === currentSectionEndPosition) {
                    currentSectionEndPosition++;
                }

                while (nextPageStartPosition < currentSectionEndPosition) {

                    let nextPageEndPosition: number = nextPageStartPosition + pageSize >= currentSectionEndPosition ? currentSectionEndPosition : nextPageStartPosition + pageSize;
                    let navItem: IVanillaReaderNavigationItem | undefined = this._getNearestNavItemFromNavCollection(nextPageStartPosition, NavigationCollectionType.TOC);
                    let navItems: IVanillaReaderNavigationItem[] | undefined = navItem ? [navItem] : undefined;
                    let locator: string | undefined =  (await this._vanillaReaderProgressionTimeline.fetchContentLocationAsRange(nextPageStartPosition, nextPageEndPosition))?.getLocator().toString();

                    pageList.push({
                        label: (currentPageIndex + 1).toString(),
                        index: currentPageIndex,
                        navItems: navItems,
                        range: new IntRange(nextPageStartPosition,  nextPageEndPosition),
                        locatorData: locator,
                        section: new IntRange(section.range.start, section.range.end)
                    });

                    currentPageIndex++;
                    nextPageStartPosition = nextPageEndPosition;
                }
            }
        }

        return pageList;
    }

    private async _buildSyntheticPageListFromNavTreeData(navigationTreeData: IVanillaReaderNavigationTreeData): Promise<IVanillaReaderSyntheticPageData[]> {
        let pages: IVanillaReaderSyntheticPageData[] | undefined;
        pages = [];
        let currentPageIndex = 0;
        let pageListCollection = navigationTreeData.collections.find((item: IVanillaReaderNavigationCollection) => {
            return item.type === NavigationCollectionType.PAGE_LIST;
        });

        if(!pageListCollection) {
            console.warn('VanillaReaderPublicationPageList._buildSyntheticPageListFromNavTreeData(), navigation tree data has no page-list collection');
            return [];
        }

        for(let page of pageListCollection.children) {

            if(page.timelineStartPosition !== undefined) {

                let nextPageIndex = Math.min(currentPageIndex + 1, pageListCollection.children.length - 1);
                let nextPageStartPosition = (pageListCollection.children[nextPageIndex].timelineStartPosition || page.timelineStartPosition  + 1);

                if(nextPageStartPosition >= page.timelineStartPosition) {
                    let range = new IntRange(page.timelineStartPosition, nextPageStartPosition);
                    let integerRange: IIntegerRange | undefined = this._getSectionFromTimelinePosition(page.timelineStartPosition)?.range;
                    let section = integerRange ? new IntRange(integerRange.start, integerRange.end) : undefined;
                    let navItem = this._getNearestNavItemFromNavCollection(page.timelineStartPosition, NavigationCollectionType.TOC);
                    let navItems: IVanillaReaderNavigationItem[] | undefined = navItem ? [navItem] : undefined;
                    let locator =  (await this._vanillaReaderProgressionTimeline.fetchContentLocationAsRange(page.timelineStartPosition, nextPageStartPosition))?.getLocator().toString();
                    pages.push(
                        {
                            label: page.title,
                            index: currentPageIndex,
                            range,
                            section,
                            locatorData: locator,
                            navItems
                        }
                    );
                }

            } else {
                console.warn(`VanillaReaderPublicationPageList._buildSyntheticPageListFromNavTreeData, Page has no start position`, page);
            }

            currentPageIndex++;
        }

        return pages;

    }

    private _getNearestNavItemFromNavCollection(position: number, collectionType: NavigationCollectionType): IVanillaReaderNavigationItem | undefined  {
        if (this._navigationTreeData) {
            let tree = this._navigationTreeData;

            if (!tree) {
                console.warn('VanillaReaderPublicationPageList._getNearestNavItemFromNavCollection(), No navigation tree data.')
                return undefined;
            }

            let collection = tree.collections.find(collection => collection.type === collectionType);

            if(!collection) {
                console.warn('VanillaReaderPublicationPageList._getNearestNavItemFromNavCollection(), Publication has no TOC.')
                return undefined;
            }

            let currentPos = 0;
            let foundItem: IVanillaReaderNavigationItem | undefined = undefined;

            while (currentPos < collection.children.length) {
                let currentItem = collection.children[currentPos];

                if(position === currentItem.timelineStartPosition) {
                    foundItem = currentItem;
                    break;
                }

                if (currentItem.timelineStartPosition === undefined || position > currentItem.timelineStartPosition) {
                    currentPos++;
                } else {
                    // If the position is greater than the current items position we return the previous nav item,
                    // otherwise we return the current position.
                    let itemIndex = currentItem.timelineStartPosition > position ? currentPos - 1 : currentPos;
                    foundItem = collection.children[itemIndex];
                    break;
                }
            }
            return foundItem;
        }
        return undefined;
    }

    private _getSectionFromTimelinePosition(position: number):  IVanillaReaderReadingProgressionTimelinePositionData | undefined  {
        if(!this._timelineData || !this._timelineData.sections) {
            console.warn('VanillaReaderPublicationPageList._getSectionFromTimelinePosition(), Timeline data does not exist, or has no sections data.');
            return;
        }
        return this._timelineData.sections.find(section => position >= section.range.start && position < section.range.end);
    }

}
